/**
 * The one line a message is summed up in (D343) — the sidebar row, the header
 * over a reply, the notification — with the words a spoiler hides marked as
 * hidden, so that none of them says what the bubble hides. The desktop's
 * `state/preview.rs`, rule for rule; offsets are UTF-16 units, which is
 * JavaScript's own string index, so nothing here converts.
 *
 * A line about a photograph or a film also carries the picture a chat-list
 * row draws in front of the words (D396, `PreviewThumb`), as Telegram's list
 * does; every other surface draws the words alone.
 */
import type { Entity } from "../gen/dto";

/** A one-line summary of a message, and which of its words a spoiler hides. */
export interface Preview {
  /** Every word of the line, the hidden ones too. What a test compares; never
   * what a surface draws as it stands — that is `previewPieces` or
   * `maskedPreview`. */
  text: string;
  /** `[from, to)` into `text` that a spoiler covers: in order, apart, never
   * empty, never through a surrogate pair. */
  hidden: Array<[number, number]>;
  /** The picture of the message's first photograph or film (D396). */
  thumb?: PreviewThumb;
  /** How many leading units of `text` are the kind's icon and its space
   * («📷 »), which a row drawing `thumb` leaves out: the picture says «photo»
   * better than the emoji did. Absent for a caption. */
  icon?: number;
}

/** The picture a line about a photograph or a film opens with in the chat
 * list (D396). An id and not a URL: the line is state, and which URL serves a
 * channel's file is the row's to say. */
export interface PreviewThumb {
  /** A photograph's 128 px thumbnail (D233), or the photograph itself when it
   * came without one; a film's poster. */
  fileId: string;
  /** Sent under a spoiler: the picture is blurred, as the bubble blurs it. */
  spoiler: boolean;
  /** A round video note — drawn as a circle. */
  round: boolean;
}

/** What stands for a hidden character in `maskedPreview`: braille cells,
 * varied so a run of them reads as the speckle of a spoiler rather than as a
 * word, and no letter of any alphabet. One UTF-16 unit each (U+28xx). */
const MASK = "⠕⡪⢅⠪⡡⢕⠣";

/** A line with nothing in it to hide — a service notice, an attachment. */
export function plainPreview(text: string): Preview {
  return { text, hidden: [] };
}

/** The line for a message with no words of its own — «📷 фото ×2» — and the
 * picture it may open with instead of its icon. */
export function attachmentPreview(text: string, thumb: PreviewThumb | null): Preview {
  if (!thumb) return plainPreview(text);
  const space = text.indexOf(" ");
  return { text, hidden: [], thumb, icon: space < 0 ? 0 : space + 1 };
}

/** The same line with a picture in front of it — a caption's line, whose
 * words all stay. */
export function withThumb(line: Preview, thumb: PreviewThumb | null): Preview {
  return thumb ? { text: line.text, hidden: line.hidden, thumb } : line;
}

/** The longest prefix of `s` at most `units` UTF-16 units long that does not
 * end on the first half of a surrogate pair. */
export function prefixUnits(s: string, units: number): string {
  if (s.length <= units) return s;
  const code = s.charCodeAt(units - 1);
  return s.slice(0, code >= 0xd800 && code <= 0xdbff ? units - 1 : units);
}

/** The spoilers among `entities` that start inside the first `units` UTF-16
 * units of their text, each cut to end there — `echo_wire::text::
 * spoilers_within`, which is what the gateway puts on a reply's quote and
 * what this client puts on the quotes it writes itself. */
export function spoilersWithin(entities: readonly Entity[], units: number): Entity[] {
  const out: Entity[] = [];
  for (const e of entities) {
    if (e.type !== "spoiler" || !(e.length > 0) || !(e.offset < units)) continue;
    out.push({
      type: "spoiler",
      offset: e.offset,
      length: Math.min(e.offset + e.length, units) - e.offset,
    });
  }
  return out;
}

/** Whether `at` falls between the two halves of a surrogate pair. */
function splitsPair(s: string, at: number): boolean {
  if (at <= 0 || at >= s.length) return false;
  const code = s.charCodeAt(at - 1);
  return code >= 0xd800 && code <= 0xdbff;
}

/** The line for `text` under `entities`: all of it when it fits in `units`
 * UTF-16 units, otherwise its start and an ellipsis in the last unit — never
 * through a surrogate pair. The spoilers come along, cut where the words are
 * cut; every other entity stays behind. A spoiler whose edge a gateway put
 * inside a pair is drawn plain rather than thrown on. */
export function cutPreview(text: string, entities: readonly Entity[], units: number): Preview {
  const long = text.length > units;
  const head = long ? prefixUnits(text, units - 1) : text;
  const spans = spoilersWithin(entities, head.length)
    .map((e): [number, number] => [e.offset, e.offset + e.length])
    .filter(([from, to]) => from < to && !splitsPair(head, from) && !splitsPair(head, to))
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  // Merged, so that two touching spoilers blur as one span — as they do in
  // the bubble, where the normal form has merged them already.
  const hidden: Array<[number, number]> = [];
  for (const [from, to] of spans) {
    const last = hidden[hidden.length - 1];
    if (last && from <= last[1]) last[1] = Math.max(last[1], to);
    else hidden.push([from, to]);
  }
  return { text: long ? `${head}…` : head, hidden };
}

/** The line in order, each piece with whether a spoiler hides it — for a
 * surface that draws the hidden pieces the way the bubble draws a spoiler it
 * has not been asked to show. */
export function previewPieces(line: Preview): Array<{ text: string; hidden: boolean }> {
  const out: Array<{ text: string; hidden: boolean }> = [];
  let at = 0;
  for (const [from, to] of line.hidden) {
    if (from > at) out.push({ text: line.text.slice(at, from), hidden: false });
    out.push({ text: line.text.slice(from, to), hidden: true });
    at = to;
  }
  if (at < line.text.length) out.push({ text: line.text.slice(at), hidden: false });
  return out;
}

/** `previewPieces` for a row that draws `line.thumb` in front of them:
 * without the icon the picture stands in for. */
export function piecesBesideThumb(line: Preview): Array<{ text: string; hidden: boolean }> {
  let skip = line.thumb ? (line.icon ?? 0) : 0;
  const out: Array<{ text: string; hidden: boolean }> = [];
  for (const piece of previewPieces(line)) {
    const cut = Math.min(skip, piece.text.length);
    skip -= cut;
    const rest = piece.text.slice(cut);
    if (rest !== "") out.push({ text: rest, hidden: piece.hidden });
  }
  return out;
}

/** The line as one string, with every hidden character but a space turned
 * into a braille cell — for a surface that can draw nothing but a string, the
 * browser's notification. The words go; how much is hidden, and where the gaps
 * between the words were, stays, which is what the blur shows too. */
export function maskedPreview(line: Preview): string {
  let out = "";
  for (const piece of previewPieces(line)) {
    if (!piece.hidden) {
      out += piece.text;
      continue;
    }
    let i = 0;
    for (const c of piece.text) {
      out += /\s/u.test(c) ? c : MASK.charAt(i % MASK.length);
      i += 1;
    }
  }
  return out;
}
